import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useCallStatus, useParticipants } from '@/entities/call'
import { lobbyQueryOptions } from '@/entities/meeting'
import { playNotifySound } from '@/shared/lib'
import { toast } from '@/shared/ui/toast'

// Уведомление (тост по центру сверху + звук) о новом удалённом участнике.
// Присутствующие на момент подключения — базлайн, о них не сообщаем; дальше
// каждое появление нового id — «присоединился к встрече».
export function useParticipantJoinNotifications(): void {
  const status = useCallStatus()
  const participants = useParticipants()
  const seenRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (status !== 'connected') return
    const remote = participants.filter((p) => !p.isLocal)
    if (seenRef.current === null) {
      seenRef.current = new Set(remote.map((p) => p.id))
      return
    }
    const seen = seenRef.current
    const joined = remote.filter((p) => !seen.has(p.id))
    for (const p of remote) seen.add(p.id)
    if (joined.length === 0) return
    for (const p of joined) toast.notify(`${p.name} присоединился к встрече`)
    playNotifySound()
  }, [status, participants])

  // Реконнект/новый звонок — базлайн собирается заново.
  useEffect(() => {
    if (status === 'disconnected') seenRef.current = null
  }, [status])
}

// Уведомление о новом госте в lobby-очереди — поллим на уровне страницы
// (сайдбар на узких экранах закрыт, его poll не живёт). Гости, уже ждавшие
// на момент входа, — базлайн: их видно в бейдже вкладки «Ожидают».
export function useLobbyNotifications(meetingId: string, enabled: boolean): void {
  const { data } = useQuery({ ...lobbyQueryOptions(meetingId), enabled })
  const seenRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (!enabled || data === undefined) return
    if (seenRef.current === null) {
      seenRef.current = new Set(data.map((g) => g.participantId))
      return
    }
    const seen = seenRef.current
    const fresh = data.filter((g) => !seen.has(g.participantId))
    for (const g of data) seen.add(g.participantId)
    if (fresh.length === 0) return
    for (const g of fresh) toast.notify(`${g.name} хочет присоединиться как гость`)
    playNotifySound()
  }, [enabled, data])
}
